import { SetStateAction, useCallback, useState } from 'react';

import { CollectionAsset, useRecommendations, useRecommendationsSettings } from '@kaltura-ott/tvpil-shared';

import { RailProviderData, RailSettings } from 'components/widgets/Rail/components/RailProvider/types';
import { useHandleSkeletonRef } from 'components/widgets/Rail/hooks/useHandleSkeletonRef/useHandleSkeletonRef';
import { DEFAULT_IMAGE_PARAMS, RAIL_PAGE_SIZE, WIDGET_TYPE } from 'consts';
import { useCheckDataIsEmpty } from 'hooks/common/useCheckDataIsEmpty/useCheckDataIsEmpty';
import { useRhinoErrorReaction } from 'hooks/common/useRhinoErrorReaction';

/**
 * `useRecommendationRail` is a custom React hook used for retrieving and managing data necessary
 * for displaying a recommendation rail in a web application. This hook combines recommendations settings,
 * channel data, and rail configuration.
 *
 * @param referenceId - A unique identifier of the recommendation rail widget.
 * @param pageReferenceId - A unique identifier of the page containing the recommendation rail widget.
 *
 * @returns An object containing:
 * - `settings`: Configurations and settings for the recommendations component's UI, obtained from `useRecommendationsSettings`.
 * - `railData`: An object structured as `RailProviderData<CollectionAsset>`, containing the CollectionAsset,
 *               total count, and loading state.
 * - `railType`: Set to `WIDGET_TYPE.RECOMMENDATION`
 *               in this context, marking it as a RECOMMENDATIONS-type rail component.
 */

function useRecommendationRail(referenceId: string) {
  const settings = useRecommendationsSettings({
    referenceId,
    ...DEFAULT_IMAGE_PARAMS
  });
  const data = useRecommendations({
    referenceId,
    pageSize: RAIL_PAGE_SIZE,
    useLimit: true,
  });

  const railType = WIDGET_TYPE.RECOMMENDATION;
  const [shouldPreventDisplay, setShouldPreventDisplay] = useState(true);
  const setShouldPreventDisplayForward = useCallback(
    (value: SetStateAction<boolean>) => setShouldPreventDisplay(value),
    [],
  );
  const isLoading = data.isLoading && !data.items.length;
  const isRailHidden = useCheckDataIsEmpty(data) || !!data.error;

  const railData: RailProviderData<CollectionAsset> = {
    items: data.items,
    totalCount: data.totalCount,
    hasNextPage: data.hasNextPage,
    isLoading: data.isLoading,
    error: data.error,
    fetchNextPage: data.fetchNextPage,
    refetch: data.refetch,
  };

  const railSettings: RailSettings = {
    branding: settings?.branding,
    id: settings?.id,
    imageHeight: settings?.imageHeight,
    imageType: settings?.imageType,
    imageWidth: settings?.imageWidth,
    title: settings?.title,
    moreLink: settings?.moreLink,
    shouldShowRating: settings?.shouldShowRating,
  };

  const handleSkeletonRef = useHandleSkeletonRef(setShouldPreventDisplayForward, data.items, railSettings);

  useRhinoErrorReaction({ error: data.error, refetch: data.refetch });

  return {
    railData,
    railType,
    railSettings: settings ? railSettings : null,
    handleSkeletonRef,
    shouldPreventDisplay,
    isRailHidden,
    isLoading,
  };
}

export { useRecommendationRail };
